import React, { useEffect, useMemo, useState } from "react";
import { X, Smartphone, Copy, Check, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * HandoffModal — issues a 60-second handoff token and renders:
 *  - a deep-link that the user can open on their other device (with #t=... fragment)
 *  - a QR code (via api.qrserver.com — no dependency add) of the same link
 *  - "Copy link" and "Regenerate" controls
 */
export default function HandoffModal({ open, onClose, destinationPath = "/home" }) {
  const [token, setToken] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [target, setTarget] = useState("mobile"); // 'mobile' | 'web'

  const issue = async () => {
    try {
      const { data } = await api.post("/auth/handoff-issue");
      setToken(data.handoff_token);
      setExpiresAt(Date.now() + (data.expires_in_sec ?? 60) * 1000);
      setCopied(false);
    } catch { toast.error("Failed to issue handoff token"); }
  };

  useEffect(() => { if (open) issue(); }, [open]);
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [open]);

  const link = useMemo(() => {
    if (!token) return "";
    // Mobile: deep-link URL scheme registered by bharat-register app.
    // Web: absolute HTTPS URL that <HandoffIngest/> on the web root will pick up.
    if (target === "mobile") {
      return `bharatregister://register${destinationPath === "/home" ? "" : destinationPath}#t=${encodeURIComponent(token)}`;
    }
    return `${window.location.origin}${destinationPath}#t=${encodeURIComponent(token)}`;
  }, [token, destinationPath, target]);

  const remaining = expiresAt ? Math.max(0, Math.round((expiresAt - now) / 1000)) : 0;
  const expired = expiresAt && remaining === 0;

  const qrSrc = link ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(link)}` : null;

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied — paste on your other device");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed — long-press to select the link"); }
  };

  if (!open) return null;

  return (
    <div data-testid="handoff-modal" className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(24,18,16,0.7)" }}>
      <div className="w-full max-w-lg" style={{ background: "var(--ivory)", border: "1px solid var(--gold)" }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid var(--line)" }}>
          <div className="flex items-center gap-2">
            <Smartphone size={16} style={{ color: "var(--crimson)" }}/>
            <p className="font-serif text-xl" style={{ color: "var(--crimson)" }}>Continue on your other device</p>
          </div>
          <button data-testid="handoff-close" onClick={onClose} style={{ color: "var(--ash)" }}><X size={18}/></button>
        </div>

        <div className="px-6 py-6">
          <p className="text-sm" style={{ color: "var(--ash)" }}>
            Scan this QR from your other device (or paste the link) to continue your Vedika session — no OTP needed.
          </p>

          <div className="mt-4 inline-flex" style={{ border: "1px solid var(--line)" }} data-testid="handoff-target-toggle">
            <button type="button" data-testid="handoff-target-mobile" onClick={() => setTarget("mobile")}
              className="text-[10px] uppercase tracking-widest px-3 py-1.5"
              style={{ background: target === "mobile" ? "var(--crimson)" : "transparent", color: target === "mobile" ? "var(--ivory)" : "var(--ash)" }}>
              Open on Mobile
            </button>
            <button type="button" data-testid="handoff-target-web" onClick={() => setTarget("web")}
              className="text-[10px] uppercase tracking-widest px-3 py-1.5"
              style={{ background: target === "web" ? "var(--crimson)" : "transparent", color: target === "web" ? "var(--ivory)" : "var(--ash)" }}>
              Open on Web
            </button>
          </div>

          <div className="mt-6 flex items-center gap-6">
            <div className="w-[220px] h-[220px] shrink-0" style={{ border: "1px solid var(--line)", background: "#fff" }}>
              {qrSrc && !expired ? (
                <img data-testid="handoff-qr" src={qrSrc} alt="Handoff QR" width={220} height={220}/>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs uppercase tracking-widest" style={{ color: "var(--ash)" }}>
                  {expired ? "Link expired" : "Loading…"}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Deep-link</p>
              <div className="mt-1 p-2 text-[11px] break-all" style={{ background: "var(--bone)", border: "1px solid var(--line)", color: "var(--ink)" }} data-testid="handoff-link">
                {link || "…"}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-[11px]" style={{ color: expired ? "var(--crimson)" : "var(--leaf)" }}>
                <Clock size={12}/> {expired ? "Expired · regenerate below" : `Valid for ${remaining}s`}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button data-testid="handoff-copy" onClick={copy} disabled={!link || expired} className="btn-primary text-[11px] py-2 px-3">
                  {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy link</>}
                </button>
                <button data-testid="handoff-regenerate" onClick={issue} className="text-[11px] uppercase tracking-widest inline-flex items-center gap-1" style={{ color: "var(--crimson)" }}>
                  <RefreshCw size={12}/> Regenerate
                </button>
              </div>
            </div>
          </div>

          <p className="mt-6 text-[11px]" style={{ color: "var(--ash)" }}>
            One-time use · 60-second window · only your logged-in account is transferred.
          </p>
        </div>
      </div>
    </div>
  );
}
